import { useEffect } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { useEditor } from "../hooks/useEditor";
import { useUi } from "../state/ui";
import { ChordChart } from "./ChordChart";
import { ChordGrid } from "./ChordGrid";
import { AdSlot } from "./AdSlot";
import { AD_SLOTS } from "../ads/adsense";
import { ScoreLinesIcon, ChordIcon } from "./icons";
import { useT } from "../i18n";

// The score section with a view toggle: full notation (alphaTab) ⇄ lead-sheet chord grid.
// alphaTab stays mounted (just hidden) in chord view so ScoreController & the cursor loop keep working.
export function ScoreSection() {
  const c = useControllers();
  const t = useT();
  const title = useProjectStore((s) => s.title);
  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);
  const view = useUi((s) => s.scoreView);
  const setView = useUi((s) => s.setScoreView);
  const ed = useEditor();
  // editing only exists in 타브악보 view; leaving it must close the editor dock too
  const toChord = () => { ed.exitEditMode(); setView("chord"); };

  // the alphaTab score is mounted but display:none in chord view (0 width) — force a re-render
  // when it becomes visible, otherwise it stays blank.
  useEffect(() => {
    if (view === "score") requestAnimationFrame(() => c.score.relayout());
  }, [view, c]);

  // nothing analyzed yet → a quiet prompt instead of an endless loading spinner
  if (!hasChart) {
    return (
      <section className="section">
        <h2>{t("score.title")}</h2>
        <div className="score-empty">{t("score.empty")}</div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section-head">
        <h2>{t("score.title")}</h2>
        <div className="view-toggle" role="group">
          <button className={"view-seg" + (view === "chord" ? " on" : "")} onClick={toChord} title={t("score.chord")}>
            <ChordIcon size={14} /><span>{t("score.chord")}</span>
          </button>
          <button className={"view-seg" + (view === "score" ? " on" : "")} onClick={() => setView("score")} title={t("score.tab")}>
            <ScoreLinesIcon size={14} /><span>{t("score.tab")}</span>
          </button>
        </div>
      </div>
      {title && <div className="score-subtitle">{title}</div>}
      <div className="score-layout">
        <div className="score-main">
          {/* collapse (not display:none) so alphaTab keeps full width & renders even while hidden */}
          <div className={"score-host" + (view === "score" ? "" : " collapsed")}><ChordChart /></div>
          {view === "chord" && <ChordGrid />}
        </div>
        {/* banner beside the chord chart (in-app only; renders nothing until AdSense is configured) */}
        <AdSlot slot={AD_SLOTS.scoreRail} className="ad-rail" />
      </div>
    </section>
  );
}
